/**
 * RelatedProductsSection Component
 *
 * Displays related/cross-sell products on product detail pages.
 * Shows curated recommendations plus algorithmically-matched products
 * based on shared tags and categories.
 *
 * Ordering rule: when the user has a pickup location selected, items
 * stocked at that location come first; items not stocked there are
 * still shown (slot-permitting) — with a "Not in <Location>" treatment
 * from ProductCard — so the user discovers them but knows they'd need
 * to change locations to rent them. Discontinued items
 * (isAvailableForRental === false) are excluded entirely; renting them
 * would never be possible from any location.
 */

import { useMemo } from 'react';
import { useRelatedProducts } from '@/hooks/useCatalog';
import { useLocationStore } from '@/stores/useLocationStore';
import { ProductCard } from './ProductCard';
import { Sparkles } from 'lucide-react';

interface RelatedProductsSectionProps {
    equipmentTypeId: string;
    title?: string;
    maxItems?: number;
}

const SkeletonCard = () => (
    <div
        className="overflow-hidden rounded-[1.75rem] border border-border/70 bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(249,245,239,1)_100%)] shadow-card"
        aria-hidden="true"
    >
        <div className="delight-skeleton aspect-[4/5]" />
        <div className="space-y-3 p-5">
            <div className="delight-skeleton h-3 w-1/3 rounded-full" />
            <div className="delight-skeleton h-5 w-4/5 rounded-full" />
            <div className="delight-skeleton h-4 w-3/5 rounded-full" />
            <div className="border-t border-border/70 pt-3">
                <div className="delight-skeleton h-3 w-1/4 rounded-full" />
                <div className="delight-skeleton mt-2 h-7 w-2/5 rounded-full" />
            </div>
        </div>
    </div>
);

export const RelatedProductsSection = ({
    equipmentTypeId,
    title = "You Might Also Like",
    maxItems = 4,
}: RelatedProductsSectionProps) => {
    const { location } = useLocationStore();
    // Over-fetch so the sort below has more candidates to draw from when
    // some items are unavailable at the active location.
    const fetchTake = Math.max(maxItems * 2, 8);
    const { data: relatedProducts, isLoading, error } = useRelatedProducts(
        equipmentTypeId,
        fetchTake,
    );

    // Sort: available-at-location first, then unavailable-at-location.
    const orderedProducts = useMemo(() => {
        if (!relatedProducts) return [];
        const rentable = relatedProducts.filter((p) => p.isAvailableForRental);
        if (!location) return rentable.slice(0, maxItems);
        const sorted = [...rentable].sort((a, b) => {
            const aAvail = a.availableQuantity > 0 ? 0 : 1;
            const bAvail = b.availableQuantity > 0 ? 0 : 1;
            return aAvail - bAvail;
        });
        return sorted.slice(0, maxItems);
    }, [relatedProducts, location, maxItems]);

    // Don't render anything if the API errored or returned nothing usable.
    // (Mirrors the prior behaviour — keeps the page tidy when there are no
    // good cross-sells. The skeleton state only shows while we're actively
    // loading; once we know there's nothing to show, the section hides.)
    if (error) return null;
    if (!isLoading && orderedProducts.length === 0) return null;

    return (
        <section className="mt-10 border-t border-border pt-10" aria-label="Related products">
            <div className="flex items-center gap-2 mb-6">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-medium text-foreground">{title}</h3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {isLoading
                    ? Array.from({ length: maxItems }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
                    : orderedProducts.map((product, index) => (
                        <div
                            key={product.id}
                            className={['delight-rise', 'delight-rise delight-delay-1', 'delight-rise delight-delay-2', 'delight-rise delight-delay-3'][index] ?? 'delight-rise delight-delay-4'}
                        >
                            <ProductCard product={product} />
                        </div>
                    ))}
            </div>
        </section>
    );
};

export default RelatedProductsSection;
